import { css } from 'styled-components';
import { Row } from '@tanstack/react-table';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import { BaseLeaderboardEntity } from '@paradigm/features/src/leaderboard/entities/shared';

const commonAfterStyles = css`
  top: 0px;
  right: 0px;
  position: absolute;
  height: 100%;
  width: 100%;
  content: '';
  border-radius: 7px;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    border-radius: 0px;
  }
`;

const commonPadding = css`
  td,
  th {
    padding: 0;
    padding-left: 1rem;
  }

  th:first-child,
  td:first-child,
  th:last-child,
  td:last-child {
    padding: 0;
  }
`;

export default function buildStyles(tableRowAlign: 'default' | 'right') {
  const firstRowsBackground = css`
    z-index: 1;
    :after {
      ${commonAfterStyles}
      z-index: -1;
      background: linear-gradient(
        131.42deg,
        rgba(56, 125, 255, 0) 0%,
        rgba(56, 144, 255, 0.15) 50.03%,
        rgba(28, 11, 88, 0.46) 100%
      );
    }
  `;

  const userRowBackground = css`
    position: relative;
    border-radius: 0px;
    z-index: 1;

    :after {
      ${commonAfterStyles}
      z-index: -1;
      opacity: 0.4;
      background: linear-gradient(
        131.42deg,
        rgba(56, 125, 255, 0) 0%,
        rgba(56, 144, 255, 0.03) 10.03%,
        rgba(28, 11, 88, 0.46) 100%
      );
      box-shadow: inset 0 0 16px 0 rgba(56, 119, 255, 0.5),
        0 0 20px 0 rgba(204, 56, 255, 0.1);
      mask: linear-gradient(to left, black 80%, transparent 100%);
      @media (max-width: ${MOBILE_BREAKPOINT}px) {
        mask: unset;
      }
    }
  `;

  const tableCss = css`
    border-collapse: separate;
    border-spacing: 1rem 6px;
    max-height: 100%;
    position: relative;
    background-color: transparent;
    margin-top: 1rem;

    @media (max-width: ${MOBILE_BREAKPOINT}px) {
      padding: 0px;
      overflow: auto;
    }
  `;

  const tHeadCss = css`
    height: 32px;
    padding: 0px;
    background-color: transparent;

    @media (max-width: ${MOBILE_BREAKPOINT}px) {
      display: table-header-group;
    }
  `;

  const thRowCss = css`
    ${commonPadding};

    height: 32px;
    box-shadow: none;
    display: table-row;
    color: #ffffff;
    font-family: 'IBM Plex Sans';
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 0;
    line-height: 16px;
    padding: 0px 16px;
    text-align: right;

    th {
      white-space: nowrap;
      &:nth-child(5),
      &:nth-child(7),
      &:nth-child(9) {
        > div > div {
          justify-content: flex-end;
        }
      }
    }
  `;

  const containerCss = css`
    max-width: 100%;
    box-sizing: border-box;
    overflow: auto;
    @media (max-width: ${MOBILE_BREAKPOINT}px) {
      overflow: auto;
    }
    position: relative;
  `;

  const trCss = <T extends BaseLeaderboardEntity>(
    row: Row<T>,
    index: number,
  ) => {
    const isOwnFirm = row.original.isFromUser;
    const isFirstThreeRank = index + 1 <= 3;
    const isFirst50 = index + 1 <= 50;
    const shouldDisplayRow = isFirst50 || isOwnFirm;

    return css`
      ${commonPadding}

      color: #ffffff;
      font-family: 'IBM Plex Sans';
      font-size: 13px;
      font-weight: 500;
      letter-spacing: 0;
      line-height: 12px;

      height: 49px;
      box-shadow: none;
      position: relative;

      td {
        width: unset;
        white-space: nowrap;

        &:nth-child(3) {
          width: 100%;
        }

        ${tableRowAlign === 'right'
          ? css`
              &:nth-child(n + 3) {
                text-align: right;
              }
            `
          : css`
              &:nth-child(5),
              &:nth-child(7),
              &:nth-child(9) {
                text-align: right;
              }
            `}
      }

      @media (max-width: ${MOBILE_BREAKPOINT}px) {
        border-radius: 0px;
        box-shadow: unset;
        margin-bottom: 10px;
        display: table-row;
      }

      ${isOwnFirm ? userRowBackground : ''};
      ${isFirstThreeRank ? firstRowsBackground : ''};
      ${!shouldDisplayRow &&
      css`
        && {
          display: none;
        }
      `}
    `;
  };

  return { tableCss, trCss, tHeadCss, thRowCss, containerCss };
}
